import { useMemo } from "react";

import { MunicipalPropertyRegisterFilter } from "@/domain/municipal-property/models/municipal-property-register-filter.model";
import { toMunicipalPropertyRegisterListInput } from "@/domain/municipal-property/models/to-municipal-property-register-list-input";
import { MunicipalPropertyRegisterListQuery } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { DEFAULT_STALE_TIME_MS } from "@/services/react-query.service/stale-time";
import { useInfiniteQuery } from "@tanstack/react-query";

import { municipalPropertyRegisterListQueryDocument } from "./municipal-property-register-list-query.gql";

interface UseMunicipalPropertyRegisterListProps {
  workspaceId: string;
  filter: MunicipalPropertyRegisterFilter;
}

export const useMunicipalPropertyRegisterList = ({ workspaceId, filter }: UseMunicipalPropertyRegisterListProps) => {
  const queryResult = useInfiniteQuery({
    queryKey: queryKeys.municipalPropertyRegisters.list({ workspaceId, filter }),
    queryFn: async ({ pageParam }): Promise<MunicipalPropertyRegisterListQuery["municipalPropertyRegisterList"]> => {
      const client = createOperationClient("MunicipalPropertyRegisterList");
      const response = await client.request(municipalPropertyRegisterListQueryDocument, {
        input: toMunicipalPropertyRegisterListInput({ workspaceId, filter, after: pageParam }),
      });
      return response.municipalPropertyRegisterList;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.hasNextPage ? (lastPage.endCursor ?? undefined) : undefined),
    enabled: !!workspaceId,
    staleTime: DEFAULT_STALE_TIME_MS,
  });

  const registerList = useMemo(
    () => (queryResult.data?.pages ?? []).flatMap((page) => page.nodeList),
    [queryResult.data?.pages],
  );

  return {
    registerList,
    totalCount: queryResult.data?.pages[0]?.totalCount ?? 0,
    isLoading: queryResult.isLoading,
    error: queryResult.error,
    hasNextPage: queryResult.hasNextPage,
    isFetchingNextPage: queryResult.isFetchingNextPage,
    fetchNextPage: queryResult.fetchNextPage,
  };
};
